import { forwardRef, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { llcApi } from '../api';

const numberToWords = (amount) => {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convertLessThanThousand = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convertLessThanThousand(n % 100) : "");
  };

  const convert = (n) => {
    if (n === 0) return "Zero";
    let result = "";
    if (n >= 1000000000) {
      result += convertLessThanThousand(Math.floor(n / 1000000000)) + " Billion ";
      n %= 1000000000;
    }
    if (n >= 1000000) {
      result += convertLessThanThousand(Math.floor(n / 1000000)) + " Million ";
      n %= 1000000;
    }
    if (n >= 1000) {
      result += convertLessThanThousand(Math.floor(n / 1000)) + " Thousand ";
      n %= 1000;
    }
    if (n > 0) result += convertLessThanThousand(n);
    return result.trim();
  };

  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);
  return `${convert(dollars)} and ${cents.toString().padStart(2, '0')}/100`;
};

export const CheckPrintView = forwardRef(({ llc, check, isPreview = false, isPreprinted = false, layout = null }, ref) => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [sigUrl, setSigUrl] = useState(null);

  // Layout with defaults
  const L = {
    top_margin: 0.0, left_margin: 0.0, check_height: 3.5,
    stub1_height: 3.75, stub2_height: 3.75, micr_bottom: 0.25,
    check_body_top: 0.95, logo_max_height: 0.7, logo_max_width: 1.0, font_scale: 1.0,
    ...(layout || {}),
  };

  useEffect(() => {
    // Clear previous URLs immediately when LLC changes
    setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setSigUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });

    if (llc?.logo_path && llc?.id) {
      llcApi.getLogo(llc.id).then(res => {
        setLogoUrl(URL.createObjectURL(res.data));
      }).catch(() => {});
    }
    if (llc?.signature_path && llc?.id) {
      llcApi.getSignature(llc.id).then(res => {
        setSigUrl(URL.createObjectURL(res.data));
      }).catch(() => {});
    }
  }, [llc?.id, llc?.logo_path, llc?.signature_path]);
  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MM/dd/yyyy');
    } catch {
      return 'MM/DD/YYYY';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const amountWords = check.amount_words || numberToWords(check.amount || 0);

  // MICR Line formatting (E13B standard)
  // A = Transit symbol (⑆) — surrounds routing number
  // C = On-Us symbol (⑈) — surrounds check number and account number
  // Format: ⑈check_number⑈  ⑆routing⑆  ⑈account⑈
  const micrLine = `C${check.check_number}C A${llc.routing_number}A C${llc.account_number}C`;

  return (
    <div ref={ref} className={`check-print-container bg-white ${isPreprinted ? 'preprinted-mode' : ''}`} style={{ width: '8.5in', minHeight: '11in', paddingTop: `${L.top_margin}in`, paddingLeft: `${L.left_margin}in`, fontSize: `${L.font_scale * 100}%` }}>
      {/* Check Section */}
      <div
        className="relative check-section-background"
        style={{
          height: `${L.check_height}in`,
          padding: '0.25in 0.3in',
        }}
      >
        {/* Security Pattern Overlay - only shows on screen */}
        <div
          className="absolute inset-0 opacity-10 check-pattern-overlay"
          style={{
            backgroundImage: 'radial-gradient(#1e3a5f 1px, transparent 1px)',
            backgroundSize: '8px 8px',
          }}
        />

        {/* Check Header - Three Column Layout */}
        <div className="relative">
          {/* Top Row: Logo+LLC (left), Bank (center-right), Check Number (right) */}
          <div className="flex items-start">
            {/* Logo + LLC Name - Left (hidden for preprinted when printing) */}
            <div className="flex-[3] flex items-start gap-2 min-w-0">
              {logoUrl && (
                <div className="mt-1 flex-shrink-0">
                  <img src={logoUrl} alt="" style={{ maxHeight: `${L.logo_max_height}in`, maxWidth: `${L.logo_max_width}in` }} className="object-contain" />
                </div>
              )}
              <div className="hide-preprinted min-w-0">
                <div className="font-bank font-bold text-base text-slate-900 mt-3 whitespace-nowrap">
                  {llc.company_name.includes(' - ') 
                    ? llc.company_name.split(' - ').map((part, i) => (
                        <div key={i}>{part.trim()}</div>
                      ))
                    : llc.company_name
                  }
                </div>
                <div className="text-xs text-slate-700">{llc.address_line1}</div>
                {llc.address_line2 && <div className="text-xs text-slate-700">{llc.address_line2}</div>}
                <div className="text-xs text-slate-700">{llc.city}, {llc.state} {llc.zip_code}</div>
              </div>
            </div>
            
            {/* Bank Info - Center, slightly right (hidden for preprinted when printing) */}
            <div className="flex-[2] text-center pl-12 mt-3">
              <div className="hide-preprinted">
                <div className="font-bank text-xs text-slate-800">{llc.bank_name}</div>
                <div className="text-[10px] text-slate-600">{llc.bank_address}</div>
                <div className="text-[10px] text-slate-600">{llc.bank_city_state_zip}</div>
                {llc.bank_phone && <div className="text-[10px] text-slate-600">{formatPhone(llc.bank_phone)}</div>}
              </div>
            </div>
            
            {/* Check Number - Top Right (hidden for preprinted when printing) */}
            <div className="font-mono text-xl font-bold text-slate-900 hide-preprinted mt-3">{check.check_number}</div>
          </div>
        </div>

        {/* Check Body - Fixed position so LLC name wrap doesn't shift it */}
        <div className="absolute flex flex-col justify-center" style={{ top: `${L.check_body_top}in`, left: '0.3in', right: '0.3in', height: '1.9in' }}>

        {/* Date */}
        <div className="relative flex justify-end mb-3">
          <div className="text-sm text-slate-600">
            <span className="mr-2 hide-preprinted">DATE</span>
            <span className="font-mono font-semibold text-slate-900 border-b border-slate-400 pb-0.5 px-2">
              {formatDate(check.date)}
            </span>
          </div>
        </div>

        {/* Pay to the Order of */}
        <div className="relative flex items-center gap-2 mb-3 pl-4">
          <span className="text-[10px] text-slate-600 whitespace-nowrap hide-preprinted">PAY TO THE<br />ORDER OF</span>
          <div className="flex-1 border-b border-slate-400 pb-1 font-bold text-sm text-slate-900 min-h-[20px]">
            {check.payee || ''}
          </div>
          <div className="flex items-center gap-1 ml-2">
            <span className="text-lg font-bold text-slate-700 hide-preprinted">$</span>
            <div className="border border-slate-400 px-3 py-1 min-w-[100px] text-right font-mono font-black text-base bg-white/50">
              {(check.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Amount in Words */}
        <div className="relative flex items-center gap-2 mb-6 pl-8">
          <div className="flex-1 pb-1 flex items-baseline min-h-[18px]">
            <span className="text-sm text-slate-900 italic font-bold whitespace-nowrap">{amountWords}</span>
            <span className="flex-1 border-b border-slate-600 ml-1 mb-0.5" style={{ borderStyle: 'solid', height: '1px' }}></span>
          </div>
          <span className="text-[10px] font-medium text-slate-700 hide-preprinted">DOLLARS</span>
        </div>

        {/* Memo & Signature */}
        <div className="relative flex justify-between items-end mt-4">
          <div className="flex items-end gap-2">
            <span className="text-[10px] text-slate-600 hide-preprinted mb-0.5">MEMO</span>
            <div className="relative w-48 min-h-[16px]">
              <div className="absolute inset-x-0 bottom-0 border-b border-slate-400 hide-preprinted"></div>
              <div className="text-xs text-slate-700 memo-text-normal relative pb-0.5">{check.memo || ''}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative border-b border-slate-400 w-56 min-h-[16px] hide-preprinted">
              {sigUrl && (
                <img
                  src={sigUrl}
                  alt=""
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 max-h-[0.5in] max-w-[2in] object-contain"
                  style={{ filter: 'brightness(0) saturate(100%) invert(14%) sepia(60%) saturate(3000%) hue-rotate(210deg) brightness(95%) contrast(95%)' }}
                />
              )}
            </div>
            <span className="text-[8px] text-slate-500 uppercase tracking-wide whitespace-nowrap hide-preprinted">AUTHORIZED SIGNATURE</span>
          </div>
        </div>
        {/* Memo text repositioned for preprinted mode */}
        <div className="memo-text-preprinted text-xs text-slate-700 mt-2 ml-16">
          {check.memo || ''}
        </div>
        </div>{/* End Check Body */}

        {/* MICR Line (hidden for preprinted when printing) */}
        <div
          className="absolute left-[0.3in] right-[0.3in] font-micr text-slate-800 hide-preprinted"
          style={{ bottom: `${L.micr_bottom}in`, fontSize: '12pt', letterSpacing: '0px', textAlign: 'center' }}
        >
          {micrLine}
        </div>
      </div>

      {/* Stub 1 - Envelope Mailing Panel (for #10 business envelope window) */}
      <div
        className="relative"
        style={{
          height: `${L.stub1_height}in`,
          padding: '0.3in 0.4in',
          background: '#FAFBFC',
          borderLeft: '1px solid #CBD5E1',
          borderRight: '1px solid #CBD5E1',
        }}
      >
        {/* Return Address - Top Left (always prints for mailing envelope) */}
        <div className="text-xs text-slate-700 mt-4 ml-6">
          <div className="font-semibold text-slate-900">{llc.company_name}</div>
          <div>{llc.address_line1}</div>
          {llc.address_line2 && <div>{llc.address_line2}</div>}
          <div>{llc.city}, {llc.state} {llc.zip_code}</div>
        </div>

        {/* Payee Address - Left aligned (for envelope window) */}
        <div 
          className="absolute"
          style={{ top: '2.0in', left: '1.6in' }}
        >
          <div className="text-left">
            <div className="font-semibold text-base text-slate-900">{check.payee || 'Payee Name'}</div>
            {check.payee_address_line1 && <div className="text-sm text-slate-700">{check.payee_address_line1}</div>}
            {check.payee_address_line2 && <div className="text-sm text-slate-700">{check.payee_address_line2}</div>}
            {check.payee_city_state_zip && <div className="text-sm text-slate-700">{check.payee_city_state_zip}</div>}
          </div>
        </div>

        {/* General Message - Right side of mailing panel */}
        {check.message && (
          <div 
            className="absolute text-right text-sm text-slate-700 italic"
            style={{ top: '1.1in', left: '3.5in', right: '0.5in' }}
          >
            {check.message}
          </div>
        )}

        {/* Check info - Bottom right (check number hidden for preprinted when printing) */}
        <div className="absolute bottom-8 right-[0.4in] text-right text-xs text-slate-500">
          <div className="hide-preprinted">Check #{check.check_number}</div>
          <div>{formatDate(check.date)}</div>
          <div className="font-mono font-semibold text-slate-700">{formatCurrency(check.amount)}</div>
        </div>

        <div className="absolute bottom-4 left-[0.3in] right-[0.3in] border-t border-dashed border-slate-300 pt-2">
          <span className="text-[9px] text-slate-400 uppercase tracking-wide">Mailing Panel - Fold to show through envelope window</span>
        </div>
      </div>

      {/* Stub 2 - Your Record */}
      <div
        className="relative"
        style={{
          height: `${L.stub2_height}in`,
          padding: '0.25in 0.3in',
          background: '#FAFBFC',
          borderLeft: '1px solid #CBD5E1',
          borderRight: '1px solid #CBD5E1',
          borderBottom: '1px solid #CBD5E1',
        }}
      >
        <div className="flex justify-between items-start border-b border-slate-200 pb-2 mb-3">
          <div className="font-semibold text-slate-900 text-sm hide-preprinted">{llc.company_name}</div>
          <div className="font-mono font-bold text-slate-900 hide-preprinted">{check.check_number}</div>
        </div>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Check #:</span>
            <span className="font-mono font-semibold">{check.check_number}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Date:</span>
            <span className="font-mono">{formatDate(check.date)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Pay to:</span>
            <span className="font-medium truncate ml-2" style={{ maxWidth: '200px' }}>{check.payee || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Amount:</span>
            <span className="font-mono font-semibold">{formatCurrency(check.amount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Memo:</span>
            <span className="truncate ml-2" style={{ maxWidth: '200px' }}>{check.memo || '-'}</span>
          </div>
          <div className="border-t border-slate-200 pt-1.5 mt-1.5 space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-500">Bank:</span>
              <span className="text-slate-700">{llc.bank_name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Routing #:</span>
              <span className="font-mono text-slate-700">{llc.routing_number || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Account #:</span>
              <span className="font-mono text-slate-700">{llc.account_number || '-'}</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-4 left-[0.3in] right-[0.3in] border-t border-dashed border-slate-300 pt-2">
          <span className="text-[9px] text-slate-400 uppercase tracking-wide">Your Record - Detach Before Depositing</span>
        </div>
      </div>

      {isPreview && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-6xl font-black text-slate-200 rotate-[-45deg] opacity-50">
            PREVIEW
          </div>
        </div>
      )}
    </div>
  );
});

CheckPrintView.displayName = 'CheckPrintView';
